import activeAdsMap from "./active-ads.json";
import { NAMED_CATEGORIES } from "../../../src/settings/ad-categories";

const isAffiliateEnabled = process.env.AFFILIATE_enabled === "true";
const isAwinEnabled = process.env.AFFILIATE_awin === "true";

// Derive AdSizeCategory directly from the single source of truth array
export type AdSizeCategory = (typeof NAMED_CATEGORIES)[number];

export type AffiliateNetwork = "awin" | "impact" | "cj" | "amazon" | "custom";

// Raw Creative Payload stored in active-ads.json with schedule window bounds
export interface RawAdPayload {
  id: string; // Creative ID (Awin 's')
  advertiserId: string; // Merchant ID (Awin 'v')
  tagId?: string; // Ad Tag/Variant ID (Awin 'q')
  network: AffiliateNetwork;
  imageUrl: string;
  width: number;
  height: number;
  dimensions?: string;
  targetUrl?: string; // Direct merchant destination fallback
  startDate?: string; // YYYY-MM-DD in Pacific Time
  endDate?: string; // YYYY-MM-DD in Pacific Time
}

// Resolved payload returned to <AffiliateAd />
export interface PromoPayload extends RawAdPayload {
  region: string;
  clickUrl: string; // Computed dynamically at runtime
}

// Pre-indexed JSON structure: Brand -> Region -> Slot/Size -> RawAdPayload[]
export type ActiveAdsCatalogMap = Record<
  string, // Brand name
  Record<
    string, // Region code
    Record<string, RawAdPayload[]> // SizeCategory -> candidate pool
  >
>;

// prettier-ignore
const EU_COUNTRY_CODES = new Set([
  "AT", "BE", "BG", "CY", "CZ", "DE", "DK", "EE", "ES", "FI",
  "FR", "GR", "HR", "HU", "IE", "IT", "LT", "LU", "LV", "MT",
  "NL", "PL", "PT", "RO", "SE", "SI", "SK", "UK", "GB",
]);

const parseScheduleDate = (value?: string, isEndDate = false): Date | null => {
  if (!value) return null;

  // Date-only schedule
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split("-").map(Number);

    return isEndDate
      ? new Date(y, m - 1, d, 23, 59, 59, 999)
      : new Date(y, m - 1, d, 0, 0, 0, 0);
  }

  // Date + time schedule
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? null : parsed;
};

// Helper: Builds standard Awin creative click URL using normalized fields and env vars
export function buildAwinCreativeUrl(item: RawAdPayload): string | null {
  if (item.network === "awin") {
    // Fall back directly to untagged target destination URL if affiliate tracking or Awin network is toggled off and there is a fallback
    if ((!isAffiliateEnabled || !isAwinEnabled) && item.targetUrl) {
      return item.targetUrl;
    }

    const publisherId = process.env.AFFILIATE_AWIN_PUBLISHER_ID;

    if (!publisherId || !item.id || !item.advertiserId) {
      console.warn(
        `[Affiliate Error] Unable to construct URL for creative ID "${item.id || "unknown"}".`,
        {
          missingCreativeId: !item.id,
          missingAdvertiserId: !item.advertiserId,
          missingPublisherIdEnv: !publisherId,
        }
      );
      return null;
    }

    const qParam = item.tagId ? `&q=${encodeURIComponent(item.tagId)}` : "";
    return `https://www.awin1.com/cread.php?s=${item.id}&v=${item.advertiserId}${qParam}&r=${publisherId}`;
  }

  if (!item.targetUrl) {
    console.warn(
      `[Affiliate Error] Non-Awin creative ID "${item.id}" lacks targetUrl.`
    );
    return null;
  }

  return item.targetUrl;
}

export function selectActiveCreative(
  brandName: string,
  size: AdSizeCategory | string = "leaderboard",
  country: string = "US"
): PromoPayload | null {
  const brandData = (activeAdsMap as ActiveAdsCatalogMap)[brandName];
  if (!brandData) return null;

  let targetCountry = country.toUpperCase();
  if (targetCountry === "GB") {
    targetCountry = "UK";
  }

  const candidateRegions: string[] = [targetCountry];
  if (targetCountry !== "EU" && EU_COUNTRY_CODES.has(targetCountry)) {
    candidateRegions.push("EU");
  }
  if (targetCountry !== "US") {
    candidateRegions.push("US");
  }

  for (const regionCode of candidateRegions) {
    const pool = brandData[regionCode]?.[size];
    if (pool && pool.length > 0) {
      // Runtime schedule evaluation for long-running static deployments.
      //
      // Schedule dates are evaluated using the visitor's local timezone.
      //
      // Date-only schedules:
      //   2026-11-02
      //   → Active when the visitor's local date reaches Nov 2.
      //
      // Date + time schedules:
      //   2026-11-02T15:00:00
      //   → Active when the visitor's local date/time reaches Nov 2 at 3:00 PM.
      //
      // Schedules are currently evaluated relative to the visitor's timezone rather
      // than a fixed Pacific-time activation window.
      const now = new Date();

      const validPool = pool.filter((ad) => {
        const startDate = parseScheduleDate(ad.startDate);
        const endDate = parseScheduleDate(ad.endDate, true);

        if (startDate && startDate > now) return false;
        if (endDate && endDate < now) return false;

        return true;
      });

      if (validPool.length > 0) {
        // Uniform random shuffle across candidate pool
        const shuffledPool = [...validPool].sort(() => Math.random() - 0.5);

        for (const selected of shuffledPool) {
          const clickUrl =
            selected.network === "awin"
              ? buildAwinCreativeUrl(selected)
              : selected.targetUrl || null;

          if (clickUrl) {
            return {
              ...selected,
              region: regionCode,
              clickUrl,
            };
          }
        }
      }
    }
  }

  return null;
}
